import React, { useContext, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Filter, ArrowDownNarrowWide, ArrowDownWideNarrow, RefreshCw } from "lucide-react";
import { listAllGiftCardRedemption } from "../../reducers/payouts.slice";
import { convertDateFormatInUtc, formatNumberToDollar, countActiveFiltersAndSort } from "../../utils/util";
import { getUsers } from "../../utils/getUser";
import {
  getAllWalletTransaction,
} from "../../reducers/wallet.slice";
import {
  createPlaidIdentityVerification,
  getPlaidIdentityVerificationStatus,
  retryPlaidIdentityVerification,
} from "../../reducers/authentication.slice";
import { AuthContext } from "../../context/authContext";
import { WalletLogo } from "../../assests/logo/walletLogo";
import { GiftCardsLogo } from "../../assests/logo/GiftCardsLogo";
import { CoinDollarLogo } from "../../assests/logo/CoinsDollor";
import { BankLogo } from "../../assests/logo/BankLogo";
import { RSC_PRIVACY_POLICY } from "../../constants/privacyPolicy";
import PaypalLogo from "../../assests/images/Paypal.png";
import bg1 from "../../assests/walletCardBg/bg1.png";
import bg2 from "../../assests/walletCardBg/bg2.png";
import bg3 from "../../assests/walletCardBg/bg3.png";
import { Button } from "../../components/ui/button";
import DataTable from "../../components/shared/DataTable";
import PageHeader from "../../components/shared/PageHeader";
import SearchBar from "../../components/shared/SearchBar";
import useMediaQuery from "../../components/shared/useMediaQuery";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
} from "../../components/ui/dropdown-menu";

const WALLET_SESSION_KEYS = {
  rowsPerPage: "walletRowsPerPage",
  selectedTypes: "walletSelectedTypes",
  sortDirection: "walletSortDirection",
};

const RETRYABLE_PLAID_STATUSES = ["failed", "expired", "canceled", "pending_review"];
const PLAID_CONSENT_POLICY_VERSION = "2026-07-08";
const getPlaidConsentStorageKey = (userId) =>
  `plaidIdentityConsentAccepted:${userId || "anonymous"}:${PLAID_CONSENT_POLICY_VERSION}`;

const statusColors = {
  CREDIT: { bg: "#388E3C20", color: "#388E3C" },
  DEBIT: { bg: "rgba(244,67,54,0.20)", color: "rgba(244,67,54)" },
  UNKNOWN: { bg: "#E0E0E020", color: "#757575" },
};

const WalletPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showLoader, setShowLoader] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const v = sessionStorage.getItem(WALLET_SESSION_KEYS.rowsPerPage);
    return v ? JSON.parse(v) : 5;
  });
  const [totalRows, setTotalRows] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedMethod, setSelectedMethod] = useState("gift-card");
  const { walletSummary, setIsWalletRefresh, isWalletRefresh, showToast } = useContext(AuthContext);
  const [walletDetail, setWalletDetail] = useState({});
  const [selectedTypes, setSelectedTypes] = useState(() => {
    const v = sessionStorage.getItem(WALLET_SESSION_KEYS.selectedTypes);
    return v ? JSON.parse(v) : [];
  });
  const [sortDirection, setSortDirection] = useState(() =>
    sessionStorage.getItem(WALLET_SESSION_KEYS.sortDirection) || null
  );
  const [showWithdrawOptions, setShowWithdrawOptions] = useState(false);
  const [showPlaidVerificationDialog, setShowPlaidVerificationDialog] = useState(false);
  const [plaidVerificationDetails, setPlaidVerificationDetails] = useState(null);

  const user = getUsers();
  const [hasAcceptedPlaidTerms, setHasAcceptedPlaidTerms] = useState(() =>
    localStorage.getItem(getPlaidConsentStorageKey(user?.id)) === "true"
  );
  const [plaidTermsChecked, setPlaidTermsChecked] = useState(false);
  const isInitialSearchRender = useRef(true);
  const isMobile = useMediaQuery("(max-width: 640px)");

  const getAllPayment = (
    page = currentPage,
    pageSize = rowsPerPage,
    transactionTypes = selectedTypes,
    amountSortDirection = sortDirection,
    search = debouncedSearch,
  ) => {
    setShowLoader(true);
    dispatch(
      getAllWalletTransaction({
        skip: page * pageSize,
        limit: pageSize,
        transaction_type: transactionTypes,
        search: search?.trim(),
        sort_by: amountSortDirection ? "AMOUNT" : "CREATED_AT",
        sort_order: amountSortDirection || "DESC",
      }),
    ).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      const walletTransactions = Array.isArray(res?.payload)
        ? res?.payload
        : res?.payload?.data || [];
      setTotalRows(res?.payload?.total || walletTransactions.length);
      const formattedPayouts = walletTransactions.map((item) => {
        const createdDate = convertDateFormatInUtc(item?.created_at, "MMMM D, YYYY hh:mm:ss A");
        const totalAmount = formatNumberToDollar(item?.amount);
        const calculatedBoost =
          ((item?.transaction_ref_details?.actual_amount -
            item?.transaction_ref_details?.discounted_amount) /
            item?.transaction_ref_details?.actual_amount) *
          100;
        return { ...item, createdDate, totalAmount, calculatedBoost };
      });
      setShowLoader(false);
      setIsWalletRefresh(!isWalletRefresh);
      setFilteredData(formattedPayouts);
    });
  };

  useEffect(() => { getAllPayment(0, rowsPerPage, selectedTypes, sortDirection, debouncedSearch); }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (isInitialSearchRender.current) { isInitialSearchRender.current = false; return; }
    setCurrentPage(0);
    getAllPayment(0, rowsPerPage, selectedTypes, sortDirection, debouncedSearch);
  }, [debouncedSearch]);

  useEffect(() => {
    setShowLoader(true);
    dispatch(listAllGiftCardRedemption()).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        showToast(res?.error?.message, "error");
        setShowLoader(false);
        return;
      }
      const totalSaved = parseFloat(
        res?.payload
          ?.reduce((sum, item) =>
            item?.status !== "REJECTED" ? sum + (item.actual_amount - item.discounted_amount) : sum, 0)
          .toFixed(2),
      );
      const totalGiftCards = res?.payload?.filter((item) => item.status !== "REJECTED")?.length || 0;
      setWalletDetail({ totalSaved, totalGiftCards });
      setShowLoader(false);
    });
  }, []);

  useEffect(() => {
    sessionStorage.setItem(WALLET_SESSION_KEYS.selectedTypes, JSON.stringify(selectedTypes));
  }, [selectedTypes]);

  useEffect(() => {
    if (sortDirection) sessionStorage.setItem(WALLET_SESSION_KEYS.sortDirection, sortDirection);
    else sessionStorage.removeItem(WALLET_SESSION_KEYS.sortDirection);
  }, [sortDirection]);

  const maskSensitiveNumber = (number) => {
    if (!number) return "****";
    const lastFour = number.slice(-4);
    const maskedPortion = "*".repeat(Math.max(0, number.length - 4));
    return `${maskedPortion}${lastFour}`;
  };

  const formatRetailerName = (retailer) => retailer?.replace(/_/g, " ");

  const handleTypeChange = (type, checked) => {
    const updatedTypes = checked ? [...selectedTypes, type] : selectedTypes.filter((t) => t !== type);
    setSelectedTypes(updatedTypes);
    setCurrentPage(0);
    getAllPayment(0, rowsPerPage, updatedTypes, sortDirection, debouncedSearch);
  };

  const handleSortAscending = () => {
    setSortDirection("asc");
    setCurrentPage(0);
    getAllPayment(0, rowsPerPage, selectedTypes, "asc", debouncedSearch);
  };

  const handleSortDescending = () => {
    setSortDirection("desc");
    setCurrentPage(0);
    getAllPayment(0, rowsPerPage, selectedTypes, "desc", debouncedSearch);
  };

  const handleChangePage = (newPage) => {
    setCurrentPage(newPage);
    getAllPayment(newPage, rowsPerPage, selectedTypes, sortDirection, debouncedSearch);
  };

  const handleChangeRowsPerPage = (n) => {
    setRowsPerPage(n);
    sessionStorage.setItem(WALLET_SESSION_KEYS.rowsPerPage, JSON.stringify(n));
    setCurrentPage(0);
    getAllPayment(0, n, selectedTypes, sortDirection, debouncedSearch);
  };

  const getNormalizedPlaidStatus = (status) => String(status || "").toLowerCase();

  const isPlaidVerificationRetryable = (details) => {
    const latestStatus = getNormalizedPlaidStatus(details?.status);
    return (
      RETRYABLE_PLAID_STATUSES.includes(latestStatus) &&
      details?.attempts_remaining > 0 &&
      !details?.manual_review_required
    );
  };

  const needsPlaidTermsAcceptance = () =>
    !plaidVerificationDetails?.identity_verification_id && !hasAcceptedPlaidTerms;

  const handleWithdrawClick = async () => {
    setShowLoader(true);
    const response = await dispatch(getPlaidIdentityVerificationStatus());
    setShowLoader(false);
    if (response?.error && Object.keys(response?.error)?.length > 0) {
      showToast(response?.error?.message, "error");
      return;
    }
    const verificationDetails = response?.payload;
    if (verificationDetails?.is_plaid_verified) { setShowWithdrawOptions(true); return; }
    setPlaidVerificationDetails(verificationDetails);
    setShowPlaidVerificationDialog(true);
  };

  const handleCheckPlaidVerificationStatus = async () => {
    setShowLoader(true);
    const response = await dispatch(getPlaidIdentityVerificationStatus());
    setShowLoader(false);
    if (response?.error && Object.keys(response?.error)?.length > 0) { showToast(response?.error?.message, "error"); return; }
    const verificationDetails = response?.payload;
    setPlaidVerificationDetails(verificationDetails);
    if (verificationDetails?.is_plaid_verified) {
      setShowPlaidVerificationDialog(false);
      setShowWithdrawOptions(true);
      showToast("Identity verification approved. You can withdraw now.", "success");
      return;
    }
    if (verificationDetails?.manual_review_required) { showToast("Your identity verification is under manual review.", "warning"); return; }
    if (isPlaidVerificationRetryable(verificationDetails)) { showToast("Identity verification was not completed successfully. You can retry now.", "warning"); return; }
    showToast("Identity verification is still in progress.", "info");
  };

  const openPlaidVerificationUrl = (shareableUrl) => {
    if (!shareableUrl) { showToast("Identity verification link is not available yet.", "error"); return; }
    window.open(shareableUrl, "_blank", "noopener,noreferrer");
  };

  const handleStartIdentityVerification = async () => {
    const latestStatus = getNormalizedPlaidStatus(plaidVerificationDetails?.status);
    if (plaidVerificationDetails?.manual_review_required) { showToast("Your identity verification is under manual review.", "warning"); return; }
    if (!plaidVerificationDetails?.identity_verification_id) {
      if (needsPlaidTermsAcceptance() && !plaidTermsChecked) { showToast("Please accept the RSC Privacy Policy and Plaid terms to continue.", "warning"); return; }
      setShowLoader(true);
      const response = await dispatch(createPlaidIdentityVerification({ gave_consent: true }));
      setShowLoader(false);
      if (response?.error && Object.keys(response?.error)?.length > 0) { showToast(response?.error?.message, "error"); return; }
      localStorage.setItem(getPlaidConsentStorageKey(user?.id), "true");
      setHasAcceptedPlaidTerms(true);
      setPlaidTermsChecked(true);
      setPlaidVerificationDetails(response?.payload);
      openPlaidVerificationUrl(response?.payload?.shareable_url);
      return;
    }
    if (isPlaidVerificationRetryable(plaidVerificationDetails)) {
      setShowLoader(true);
      const response = await dispatch(retryPlaidIdentityVerification());
      setShowLoader(false);
      if (response?.error && Object.keys(response?.error)?.length > 0) { showToast(response?.error?.message, "error"); return; }
      setPlaidVerificationDetails(response?.payload);
      openPlaidVerificationUrl(response?.payload?.shareable_url);
      return;
    }
    if (plaidVerificationDetails?.shareable_url) { openPlaidVerificationUrl(plaidVerificationDetails.shareable_url); return; }
    if (RETRYABLE_PLAID_STATUSES.includes(latestStatus)) { showToast("Identity verification retry is not available right now.", "warning"); return; }
    showToast("Please complete identity verification before withdrawing.", "warning");
  };

  const getPlaidVerificationMessage = () => {
    const latestStatus = getNormalizedPlaidStatus(plaidVerificationDetails?.status);
    if (plaidVerificationDetails?.manual_review_required)
      return "Your identity verification is under manual review. You can withdraw after verification is approved.";
    if (RETRYABLE_PLAID_STATUSES.includes(latestStatus) && plaidVerificationDetails?.attempts_remaining === 0)
      return plaidVerificationDetails?.next_available_retry_date
        ? `Your identity verification attempt limit has been reached. You can retry after ${plaidVerificationDetails.next_available_retry_date}.`
        : "Your identity verification attempt limit has been reached. Please try again later.";
    if (!plaidVerificationDetails?.identity_verification_id)
      return needsPlaidTermsAcceptance()
        ? "Review and accept the RSC Privacy Policy and Plaid terms before starting identity verification."
        : "You need to verify your identity before making a withdrawal.";
    if (isPlaidVerificationRetryable(plaidVerificationDetails))
      return "Your previous identity verification was not completed successfully. Please retry identity verification before withdrawing.";
    return "You already have an identity verification in progress. Please complete it before withdrawing.";
  };

  const getPlaidVerificationActionLabel = () => {
    if (!plaidVerificationDetails?.identity_verification_id)
      return needsPlaidTermsAcceptance() ? "Accept & Start Verification" : "Start Verification";
    if (isPlaidVerificationRetryable(plaidVerificationDetails)) return "Retry Verification";
    return "Continue Verification";
  };

  const canStartPlaidVerification = () =>
    !plaidVerificationDetails?.manual_review_required &&
    !(
      RETRYABLE_PLAID_STATUSES.includes(getNormalizedPlaidStatus(plaidVerificationDetails?.status)) &&
      plaidVerificationDetails?.attempts_remaining === 0
    ) &&
    (
      !plaidVerificationDetails?.identity_verification_id ||
      isPlaidVerificationRetryable(plaidVerificationDetails) ||
      Boolean(plaidVerificationDetails?.shareable_url)
    );

  const handleSelectedMethod = () => {
    if (selectedMethod === "gift-card") navigate("/dashboard/wallet/gift-cards");
    else if (selectedMethod === "bank") navigate("/dashboard/wallet/bank-account");
    else if (selectedMethod === "paypal") navigate("/dashboard/wallet/paypal-account");
  };

  const columns = [
    { key: "id", label: "Trans. ID", width: 100 },
    { key: "createdDate", label: "Date & Time", width: 180 },
    {
      key: "amount",
      label: "Amount",
      width: 180,
      render: (row) =>
        row?.transaction_ref_type === "REDEMPTION_REQUEST" && row?.transaction_type !== "CREDIT" ? (
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span style={{ textDecoration: "line-through", color: "#7a7a7a", fontSize: "0.875rem" }}>
              {formatNumberToDollar(row?.transaction_ref_details?.actual_amount)}
            </span>
            <span style={{ fontSize: "0.875rem" }}>
              {formatNumberToDollar(row?.transaction_ref_details?.discounted_amount)}
            </span>
            <span
              style={{
                background: "linear-gradient(to right, rgba(27,178,130,0.3), rgba(4,17,14,0))",
                borderRadius: 24,
                padding: "2px 8px",
                color: "rgba(27,178,130,1)",
                letterSpacing: 1,
                fontSize: 11,
                fontWeight: 400,
                lineHeight: 1,
              }}
            >
              Saved {row?.calculatedBoost?.toFixed ? row.calculatedBoost.toFixed(1) : row.calculatedBoost}%
            </span>
          </div>
        ) : (
          <span style={{ fontSize: "0.875rem" }}>{row?.totalAmount}</span>
        ),
    },
    {
      key: "description",
      label: "Description",
      width: 220,
      render: (row) =>
        row?.transaction_ref_type === "REDEMPTION_REQUEST" ? (
          <span style={{ fontSize: "0.875rem" }}>
            Gift Card - {formatRetailerName(row?.transaction_ref_details?.gift_card_details?.retailer)}
          </span>
        ) : row?.transaction_ref_type === "PAYOUT_REQUEST" ? (
          <span style={{ fontSize: "0.875rem" }}>
            Shipment - {row?.transaction_ref_details?.shipment_details?.id}
          </span>
        ) : row?.transaction_ref_type === "WITHDRAWAL_REQUEST" ? (
          row?.transaction_ref_details?.payment_method_details?.payment_method === "PayPal" ? (
            <span
              style={{ fontSize: "0.875rem" }}
              title="Your funds are on the way to your PayPal account! Note that PayPal withdrawals are sent within 3 business days."
            >
              {row?.transaction_ref_details?.payment_method_details?.payment_method}
            </span>
          ) : (
            <span
              style={{ fontSize: "0.875rem" }}
              title="Your funds are on the way to your bank account! Note that ACH withdrawal requests can take up to 3 business days to initiate, and are delivered to your bank between 1-5 business days"
            >
              Bank Transfer -{" "}
              {maskSensitiveNumber(row?.transaction_ref_details?.payment_method_details?.account_number)}
            </span>
          )
        ) : null,
    },
    {
      key: "transaction_type",
      label: "Type",
      width: 100,
      align: "center",
      render: (row) => {
        const { bg, color } = statusColors[row?.transaction_type] || statusColors.UNKNOWN;
        return (
          <span
            style={{
              padding: "2px 8px",
              width: "fit-content",
              borderRadius: 5,
              display: "inline-block",
              backgroundColor: bg,
              color,
            }}
          >
            {row?.transaction_type === "CREDIT" ? "Credited" : row?.transaction_type === "DEBIT" ? "Debited" : "Unknown Status"}
          </span>
        );
      },
    },
  ];

  const activeFilterCount = countActiveFiltersAndSort(sortDirection, selectedTypes);

  const withdrawMethodCards = [
    {
      id: "gift-card",
      label: "Purchase Gift Cards",
      badge: "Recommended - Save 10% or more",
      icon: <GiftCardsLogo height="30" width="30" />,
      bg: "linear-gradient(to right, rgba(82,6,88,0.8), rgba(20,0,22,1))",
      border: "1px solid rgba(65,2,70,1)",
    },
    {
      id: "bank",
      label: "Withdraw to Bank Account",
      icon: <BankLogo height="30" width="30" />,
      bg: "linear-gradient(to right, rgba(80,42,19,1), rgba(6,5,14,1))",
      border: "1px solid rgba(72,39,19,1)",
    },
    {
      id: "paypal",
      label: "Withdraw to PayPal",
      icon: <img src={PaypalLogo} alt="paypal" height="30" width="30" />,
      bg: "linear-gradient(to right, rgba(1,13,131,1), rgba(0,5,49,1))",
      border: "1px solid rgba(1,12,129,1)",
    },
  ];

  return (
    <div style={{ marginTop: 24, width: "100%" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          gap: 12,
          marginTop: 16,
        }}
      >
        <PageHeader title="My Wallet" subtitle="You can see your wallet balance from shipments here." />
        <Button onClick={handleWithdrawClick} style={{ flexShrink: 0 }}>
          Withdraw
        </Button>
      </div>

      {/* Balance cards */}
      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: 16,
          marginTop: 24,
          width: "100%",
        }}
      >
        {/* Wallet Balance card */}
        <div
          style={{
            flex: isMobile ? "none" : "4",
            background: "linear-gradient(to right, rgba(48,40,131,0.8), rgba(6,5,14,1))",
            borderRadius: 10,
            padding: 16,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            position: "relative",
            overflow: "hidden",
            minHeight: 120,
          }}
        >
          <div style={{ position: "absolute", backgroundImage: `url(${bg1})`, backgroundSize: "cover", backgroundPosition: "bottom left", width: "12%", height: "100%", bottom: 10, left: 10, zIndex: 0 }} />
          <div style={{ position: "absolute", backgroundImage: `url(${bg2})`, backgroundSize: "cover", backgroundPosition: "bottom left", width: "12%", height: "100%", top: 10, right: 10, zIndex: 0 }} />
          <div style={{ display: "flex", alignItems: "center", position: "relative", zIndex: 1 }}>
            <div style={{ backgroundColor: "rgba(255,255,255,0.11)", borderRadius: "50%", padding: 10, marginRight: 10, display: "flex" }}>
              <WalletLogo height="18" width="18" />
            </div>
            <span style={{ fontFamily: "Arial", fontSize: 14, fontWeight: "bold", letterSpacing: 1 }}>
              Wallet Balance
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "flex-end", position: "relative", zIndex: 1 }}>
            <span style={{ fontFamily: "Arial", fontSize: 18, fontWeight: 500, margin: "2px 5px 0 0" }}>$</span>
            <span style={{ fontWeight: "bold", fontSize: "2rem" }}>
              {walletSummary?.current_balance?.toLocaleString("en-US")}
            </span>
          </div>
        </div>

        {/* Gift Cards + Savings card */}
        <div
          style={{
            flex: 1,
            background: "linear-gradient(to right, rgba(82,6,88,0.8), rgba(20,0,22,1))",
            borderRadius: 10,
            padding: 16,
            display: "flex",
            alignItems: "center",
            position: "relative",
            overflow: "hidden",
            minHeight: 120,
          }}
        >
          <div style={{ position: "absolute", backgroundImage: `url(${bg3})`, backgroundSize: "cover", backgroundPosition: "bottom left", width: "12%", height: "100%", top: 10, right: 10, zIndex: 0 }} />
          {/* Gift Cards count */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between", gap: 8, position: "relative", zIndex: 1 }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <div style={{ backgroundColor: "rgba(255,255,255,0.11)", borderRadius: "50%", padding: 10, marginRight: 10, display: "flex" }}>
                <GiftCardsLogo height="20" width="20" />
              </div>
              <span style={{ fontFamily: "Arial", fontSize: 14, fontWeight: "bold", letterSpacing: 1 }}>Purchased Gift Cards</span>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <span style={{ fontWeight: "bold", fontSize: "1.5rem" }}>{walletDetail?.totalGiftCards}</span>
            </div>
          </div>
          {/* Divider */}
          <div style={{ width: 1, minHeight: 90, backgroundColor: "rgba(255,255,255,0.16)", margin: "0 16px", borderRadius: 5, position: "relative", zIndex: 1 }} />
          {/* Savings */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between", gap: 8, position: "relative", zIndex: 1 }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <div style={{ backgroundColor: "rgba(255,255,255,0.11)", borderRadius: "50%", padding: 10, marginRight: 10, display: "flex" }}>
                <CoinDollarLogo height="20" width="20" />
              </div>
              <span style={{ fontFamily: "Arial", fontSize: 14, fontWeight: "bold", letterSpacing: 1 }}>Saved Via Gift Cards</span>
            </div>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "flex-end" }}>
              <span style={{ fontFamily: "Arial", fontSize: 16, fontWeight: 500, margin: "2px 5px 0 0" }}>$</span>
              <span style={{ fontWeight: "bold", fontSize: "1.5rem" }}>{walletDetail?.totalSaved?.toLocaleString("en-US")}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Wallet History section */}
      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          gap: 12,
          marginTop: 32,
          marginBottom: 16,
        }}
      >
        <PageHeader title="Wallet History" subtitle="Keep track of all your wallet transactions at a glance." />
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0, flexWrap: "wrap" }}>
          <button
            onClick={() => getAllPayment(currentPage, rowsPerPage, selectedTypes, sortDirection, debouncedSearch)}
            style={{ background: "none", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 6, padding: "6px 8px", cursor: "pointer", display: "flex", alignItems: "center" }}
          >
            <RefreshCw size={14} />
          </button>
          <SearchBar placeholder="Search transactions…" searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="default">
                <Filter size={14} /> Filter & Sort
                {activeFilterCount > 0 && (
                  <span style={{ marginLeft: 4, display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: 16, height: 16, borderRadius: 8, background: "#00ADB5", color: "#000", fontSize: 10, fontWeight: 800, padding: "0 4px" }}>
                    {activeFilterCount}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" style={{ minWidth: 220 }}>
              <DropdownMenuLabel>Filter by Type</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {["CREDIT", "DEBIT"].map((type) => (
                <DropdownMenuCheckboxItem
                  key={type}
                  checked={selectedTypes.includes(type)}
                  onSelect={(e) => e.preventDefault()}
                  onCheckedChange={(checked) => handleTypeChange(type, checked)}
                >
                  {type}
                </DropdownMenuCheckboxItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuLabel>Sort by Amount</DropdownMenuLabel>
              <DropdownMenuItem active={sortDirection === "asc"} onClick={handleSortAscending}>
                <ArrowDownNarrowWide size={14} style={{ marginRight: 8 }} /> Low to High
              </DropdownMenuItem>
              <DropdownMenuItem active={sortDirection === "desc"} onClick={handleSortDescending}>
                <ArrowDownWideNarrow size={14} style={{ marginRight: 8 }} /> High to Low
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={filteredData}
        totalRows={totalRows}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        loading={showLoader}
        minWidth={800}
      />

      {/* Withdraw Options Dialog */}
      <Dialog open={showWithdrawOptions} onOpenChange={setShowWithdrawOptions}>
        <DialogContent style={{ maxWidth: 500 }}>
          <DialogHeader>
            <DialogTitle>Choose Withdraw Method</DialogTitle>
            <DialogDescription>
              Confirm the bank account and the amount you have submitted for withdrawal
            </DialogDescription>
          </DialogHeader>
          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 8 }}>
            {withdrawMethodCards.map((card) => (
              <div
                key={card.id}
                onClick={() => setSelectedMethod(card.id)}
                style={{
                  backgroundColor: "rgba(16,16,16,1)",
                  padding: 8,
                  borderRadius: 8,
                  marginTop: 4,
                  border: selectedMethod === card.id ? "1px solid rgba(255,255,255,0.4)" : "1px solid transparent",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                }}
              >
                <div style={{ background: card.bg, border: card.border, borderRadius: 8, padding: "20px 30px", display: "flex" }}>
                  {card.icon}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <span style={{ fontSize: 18 }}>{card.label}</span>
                  {card.badge && (
                    <span style={{ background: "linear-gradient(to right, rgba(27,178,130,0.3), rgba(4,17,14,0))", borderRadius: 5, color: "rgba(27,178,130,1)", fontSize: 12, fontWeight: "bold", padding: "2px 8px" }}>
                      {card.badge}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter style={{ marginTop: 12 }}>
            <Button onClick={handleSelectedMethod}>Continue</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Plaid Verification Dialog */}
      <Dialog open={showPlaidVerificationDialog} onOpenChange={setShowPlaidVerificationDialog}>
        <DialogContent style={{ maxWidth: 500 }}>
          <DialogHeader>
            <DialogTitle>Verify Identity</DialogTitle>
          </DialogHeader>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <p style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.9rem" }}>
              {getPlaidVerificationMessage()}
            </p>
            {needsPlaidTermsAcceptance() && (
              <>
                <div
                  style={{
                    maxHeight: 260,
                    overflowY: "auto",
                    border: "1px solid rgba(255,255,255,0.12)",
                    borderRadius: 4,
                    padding: 16,
                    backgroundColor: "rgba(255,255,255,0.04)",
                    fontSize: "0.85rem",
                    lineHeight: 1.6,
                    color: "rgba(255,255,255,0.75)",
                  }}
                >
                  <ReactMarkdown>{RSC_PRIVACY_POLICY}</ReactMarkdown>
                </div>
                <label style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer", fontSize: "0.8rem", color: "rgba(255,255,255,0.75)" }}>
                  <input
                    type="checkbox"
                    checked={plaidTermsChecked}
                    onChange={(e) => setPlaidTermsChecked(e.target.checked)}
                    style={{ marginTop: 2, accentColor: "#00ADB5" }}
                  />
                  I have read and agree to the RSC Privacy Policy and Plaid identity verification terms.
                </label>
              </>
            )}
            {plaidVerificationDetails?.attempt_number && (
              <p style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.55)" }}>
                Attempt {plaidVerificationDetails.attempt_number}
                {typeof plaidVerificationDetails?.attempts_remaining === "number"
                  ? ` - ${plaidVerificationDetails.attempts_remaining} retries remaining`
                  : ""}
              </p>
            )}
          </div>
          <DialogFooter style={{ marginTop: 16, flexWrap: "wrap", gap: 8 }}>
            <Button variant="outline" onClick={() => setShowPlaidVerificationDialog(false)}>
              Close
            </Button>
            {plaidVerificationDetails?.identity_verification_id && (
              <Button variant="outline" onClick={handleCheckPlaidVerificationStatus}>
                <RefreshCw size={14} style={{ marginRight: 4 }} /> Check Status
              </Button>
            )}
            {canStartPlaidVerification() && (
              <Button
                onClick={handleStartIdentityVerification}
                disabled={needsPlaidTermsAcceptance() && !plaidTermsChecked}
              >
                {getPlaidVerificationActionLabel()}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WalletPage;
